<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import OnboardingAPI from 'dashboard/api/onboarding';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Logo from 'dashboard/components-next/icon/Logo.vue';
import Modal from 'dashboard/components/Modal.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import OnboardingProgressBar from './OnboardingProgressBar.vue';
import Step00Welcome from './Step00Welcome.vue';
import Step01Inbox from './Step01Inbox.vue';
import Step02Agents from './Step02Agents.vue';
import Step03Teams from './Step03Teams.vue';
import Step04Distribution from './Step04Distribution.vue';
import Step05WorkingHours from './Step05WorkingHours.vue';
import Step06LeadReturn from './Step06LeadReturn.vue';
import Step07CannedResponses from './Step07CannedResponses.vue';
import Step08Templates from './Step08Templates.vue';
import Step09Csat from './Step09Csat.vue';
import Step10Labels from './Step10Labels.vue';
import Step11Conclusion from './Step11Conclusion.vue';

// Populated incrementally as each step screen ships — see OnboardingWizard.spec.js
// and the onboarding-wizard-shell PRD tasks 16-26/34.
const STEP_COMPONENTS = {
  0: Step00Welcome,
  1: Step01Inbox,
  2: Step02Agents,
  3: Step03Teams,
  4: Step04Distribution,
  5: Step05WorkingHours,
  6: Step06LeadReturn,
  7: Step07CannedResponses,
  8: Step08Templates,
  9: Step09Csat,
  10: Step10Labels,
  11: Step11Conclusion,
};

const FIRST_STEP = 1;
const LAST_STEP = 11;
const AUTO_SAVE_DEBOUNCE_MS = 2000;

const router = useRouter();
const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();

const isEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.ONBOARDING_WIZARD)
);

const isLoading = ref(true);
const canAdvance = ref(true);
const progress = ref({
  current_step: 0,
  completed_steps: [],
  deferred_steps: [],
  status: 'not_started',
  draft_data: {},
  capabilities: { ai_agent_available: false },
});

// Mirrors progress.draft_data, keyed by "step_N". Kept as a separate ref (rather than
// read directly off `progress`) so the per-step watcher below can tell a user edit apart
// from a hydration triggered by fetchProgress/persistStep — see `isHydratingDraft`.
const draftData = ref({});
const isHydratingDraft = ref(false);
let autoSaveTimeoutId = null;

const currentStep = computed(() => progress.value.current_step);
const currentStepKey = computed(() => `step_${currentStep.value}`);
const currentStepDraft = computed(() => draftData.value[currentStepKey.value]);
const activeStepComponent = computed(
  () => STEP_COMPONENTS[currentStep.value] || null
);
const showFooter = computed(
  () => currentStep.value >= FIRST_STEP && currentStep.value <= 10
);
const stepCounter = computed(() =>
  t('ONBOARDING.PROGRESS_BAR.STEP_COUNTER', {
    current: currentStep.value,
    total: LAST_STEP - 1,
  })
);

const hydrateDraftData = data => {
  isHydratingDraft.value = true;
  draftData.value = { ...data };
  nextTick(() => {
    isHydratingDraft.value = false;
  });
};

const fetchProgress = async () => {
  try {
    const response = await OnboardingAPI.get();
    progress.value = response.data;
    hydrateDraftData(response.data.draft_data);
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const persistStep = async (newStep, extraPayload = {}) => {
  try {
    const response = await OnboardingAPI.update({
      current_step: newStep,
      ...extraPayload,
    });
    progress.value = response.data;
    hydrateDraftData(response.data.draft_data);
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
};

const updateCurrentStepDraft = data => {
  draftData.value = { ...draftData.value, [currentStepKey.value]: data };
};

const saveDraftNow = async () => {
  const stepKey = currentStepKey.value;
  try {
    const response = await OnboardingAPI.update({
      draft_data: { [stepKey]: draftData.value[stepKey] },
    });
    progress.value = response.data;
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
};

const scheduleAutoSave = () => {
  if (autoSaveTimeoutId) clearTimeout(autoSaveTimeoutId);
  autoSaveTimeoutId = setTimeout(() => {
    autoSaveTimeoutId = null;
    saveDraftNow();
  }, AUTO_SAVE_DEBOUNCE_MS);
};

// Persist a pending debounced draft immediately. Navigation must call this before
// persistStep, otherwise the edit made within the debounce window is overwritten when
// persistStep re-hydrates draft_data from the server response (and the stale timer would
// later fire against the next step's key).
const flushPendingDraft = async () => {
  if (!autoSaveTimeoutId) return;
  clearTimeout(autoSaveTimeoutId);
  autoSaveTimeoutId = null;
  await saveDraftNow();
};

watch(
  currentStepDraft,
  () => {
    if (isHydratingDraft.value) return;
    scheduleAutoSave();
  },
  { deep: true }
);

onBeforeUnmount(() => {
  if (autoSaveTimeoutId) clearTimeout(autoSaveTimeoutId);
});

// Steps 1-2 (applied immediately) and the optional-gate steps 8-10 (deferred_steps) mark
// their own completion before Next is even clickable. Mandatory draft steps 3-7 don't have
// an immediate-apply resource or a gate to do that, so advancing past one here is what
// marks it complete — guarded so a step already deferred is never also marked completed.
const markCurrentStepCompletedIfNeeded = () => {
  const step = currentStep.value;
  const alreadyResolved =
    progress.value.completed_steps.includes(step) ||
    progress.value.deferred_steps.includes(step);
  if (step < FIRST_STEP || step > 10 || alreadyResolved) return {};

  return {
    completed_steps: [...new Set([...progress.value.completed_steps, step])],
  };
};

const goNext = async () => {
  canAdvance.value = true;
  await flushPendingDraft();
  await persistStep(
    Math.min(currentStep.value + 1, LAST_STEP),
    markCurrentStepCompletedIfNeeded()
  );
};

const goBack = async () => {
  canAdvance.value = true;
  await flushPendingDraft();
  await persistStep(Math.max(currentStep.value - 1, FIRST_STEP));
};

// Exit the wizard at any step (ADR-002): persist the skip so the account leaves the
// not_started/in_progress window (hides the agent modal, keeps the sidebar resume item),
// then return to the platform. current_step is left untouched so progress can be resumed.
const skipOnboarding = async () => {
  canAdvance.value = true;
  await flushPendingDraft();
  await persistStep(currentStep.value, { status: 'skipped' });
  router.replace({ name: 'home' });
};

// Manual reset for testing/QA: only rewinds the wizard's own progress tracking
// (current step, completed/deferred steps, status) back to the start. draft_data and any
// real resources already created (inbox, agents, team, etc.) are intentionally left alone —
// re-running the wizard will simply re-detect them as already done.
const resetOnboarding = async () => {
  canAdvance.value = true;
  await flushPendingDraft();
  await persistStep(0, {
    completed_steps: [],
    deferred_steps: [],
    status: 'not_started',
  });
};

// Collapses Reset/Exit behind a single compact kebab menu. Reset still requires an
// explicit confirm step (a shared Modal, since it's hard to undo); Exit runs immediately.
//
// A "Hard reset" that also deleted the wizard's own resources (inbox, team, canned
// responses, invited agents) used to live here, but it deleted them via the account-wide
// list APIs with no way to tell a wizard-created resource apart from a pre-existing
// production one — on any account with real data, confirming it would delete that data
// too. Removed rather than left in a broken state; reintroducing it needs the backend to
// track which resource IDs the wizard itself created (see PR #233 review).
const showActionsMenu = ref(false);
const pendingConfirmAction = ref(null); // null | 'reset'

const actionMenuItems = computed(() => [
  {
    label: t('ONBOARDING.RESET'),
    action: 'reset',
    value: 'reset',
    icon: 'i-lucide-rotate-ccw',
  },
  {
    label: t('ONBOARDING.EXIT'),
    action: 'exit',
    value: 'exit',
    icon: 'i-lucide-log-out',
  },
]);

const handleMenuAction = ({ value }) => {
  showActionsMenu.value = false;
  if (value === 'exit') {
    skipOnboarding();
  } else {
    pendingConfirmAction.value = value;
  }
};

const cancelPendingAction = () => {
  pendingConfirmAction.value = null;
};

const confirmPendingAction = async () => {
  const action = pendingConfirmAction.value;
  pendingConfirmAction.value = null;
  if (action === 'reset') {
    await resetOnboarding();
  }
};

onMounted(() => {
  if (!isEnabled.value) {
    router.replace({ name: 'home' });
    return;
  }
  fetchProgress();
});

defineExpose({
  progress,
  currentStep,
  draftData,
  currentStepDraft,
  goNext,
  goBack,
  skipOnboarding,
  resetOnboarding,
  fetchProgress,
  saveDraftNow,
  updateCurrentStepDraft,
});
</script>

<template>
  <div
    v-if="isEnabled"
    class="flex flex-col w-full h-screen overflow-hidden contain-layout bg-n-background"
  >
    <div
      v-if="isLoading"
      class="flex items-center justify-center w-full h-full"
    >
      <Spinner :size="32" />
    </div>
    <template v-else>
      <div
        v-if="showFooter"
        class="flex flex-col w-full gap-4 pt-5 px-8 bg-n-brand/10 md:px-16 shrink-0"
      >
        <div class="flex items-center justify-between w-full gap-4">
          <Logo class="w-full max-w-[130px] h-auto shrink-0" />
          <div class="flex items-center gap-2 shrink-0">
            <div
              v-on-clickaway="() => (showActionsMenu = false)"
              class="relative"
            >
              <Button
                icon="i-lucide-ellipsis-vertical"
                color="slate"
                variant="ghost"
                size="xs"
                :class="showActionsMenu ? 'bg-n-alpha-2' : ''"
                @click="showActionsMenu = !showActionsMenu"
              />
              <DropdownMenu
                v-if="showActionsMenu"
                :menu-items="actionMenuItems"
                class="ltr:right-0 rtl:left-0 mt-1 w-52 top-full"
                @action="handleMenuAction"
              />
            </div>
            <span class="text-sm font-medium text-n-slate-12 whitespace-nowrap">
              {{ stepCounter }}
            </span>
          </div>
        </div>
        <OnboardingProgressBar
          :current-step="currentStep"
          :completed-steps="progress.completed_steps"
          class="-mb-2.5"
        />
      </div>
      <!-- eslint-disable-next-line vue/no-root-v-if -->
      <Modal
        v-if="pendingConfirmAction"
        :show="!!pendingConfirmAction"
        :on-close="cancelPendingAction"
        show-close-button
      >
        <div class="p-6">
          <h3 class="text-lg font-medium text-n-slate-12">
            {{ t('ONBOARDING.RESET_CONFIRM') }}
          </h3>
          <p class="mt-2 text-n-slate-11">
            {{ t('ONBOARDING.RESET_HINT') }}
          </p>
          <div class="flex justify-end gap-2 mt-4">
            <Button
              variant="faded"
              color="slate"
              type="button"
              :label="t('DIALOG.BUTTONS.CANCEL')"
              @click="cancelPendingAction"
            />
            <Button
              type="button"
              color="blue"
              :label="t('DIALOG.BUTTONS.CONFIRM')"
              @click="confirmPendingAction"
            />
          </div>
        </div>
      </Modal>
      <div v-if="activeStepComponent" class="flex-1 w-full min-h-0">
        <component
          :is="activeStepComponent"
          :progress="progress"
          :draft-data="currentStepDraft"
          class="w-full h-full overflow-y-auto"
          @advance="goNext"
          @refresh="fetchProgress"
          @update:can-advance="canAdvance = $event"
          @update:draft-data="updateCurrentStepDraft"
        />
      </div>
      <div
        v-else
        class="flex items-center justify-center flex-1 w-full text-n-slate-11"
      >
        {{ currentStep }}
      </div>
      <div
        v-if="showFooter"
        class="flex items-center justify-center w-full p-4 border-t bg-n-background border-n-weak shrink-0"
      >
        <div class="flex items-center justify-between w-full max-w-2xl gap-3">
          <Button
            variant="faded"
            color="slate"
            type="button"
            icon="i-lucide-arrow-left"
            :label="t('ONBOARDING.BACK')"
            :disabled="currentStep === FIRST_STEP"
            @click="goBack"
          />
          <Button
            type="button"
            trailing-icon
            icon="i-lucide-arrow-right"
            :label="t('ONBOARDING.NEXT')"
            :disabled="!canAdvance"
            @click="goNext"
          />
        </div>
      </div>
    </template>
  </div>
  <template v-else />
</template>
